/* ============================================================
   wsp-theme.js — joins the two halves of the catalogue, and points the
   basket at Shopify.

   Loads BEFORE wsp.js and hands it a finished catalogue via
   window.WSP_CATALOGUE, so every page script written for the static site
   runs unchanged on Shopify.

     WSP_DESIGN   (theme asset)  club colours, photo treatment, signature,
                                 finish/size/frame model, prices as imported
     WSP_SHOPIFY  (Liquid)       variant ids, live prices, availability,
                                 image URLs, cart routes

   They are joined on SKU, which is why the SKU format in build-site.mjs is
   not cosmetic: it is the primary key between the design system and the
   shop.
   ============================================================ */
(function (W, D) {
  'use strict';

  const design  = W.WSP_DESIGN  || null;
  const shopify = W.WSP_SHOPIFY || { routes: {}, products: {} };
  if (!design) return;

  const R = shopify.routes || {};
  const handleOf = p => p.slug;                 // build-site.mjs uses the slug as the Shopify handle

  /* ------------------------------------------------------------------
     1. The merged catalogue
     Prices come from the design data until Shopify's own are attached —
     they were imported from the same numbers, so the page never renders a
     wrong price, only a not-yet-confirmed one.
     ------------------------------------------------------------------ */
  const cat = JSON.parse(JSON.stringify(design));

  function attach(handle, live) {
    const p = cat.products.find(x => handleOf(x) === handle);
    if (!p || !live) return;
    p.shopify = { id: live.id, url: live.url, available: live.available, images: live.images || [] };
    const bySku = Object.fromEntries((live.variants || []).map(v => [v.sku, v]));
    p.variants.forEach(v => {
      const s = bySku[v.sku];
      if (!s) return;
      v.variantId = s.id;
      v.price = s.price;
      v.available = s.available;
    });
    /* Preview images now come from Shopify's CDN. */
    const first = (live.images && live.images[0]) || live.featured;
    if (first) {
      p.previews = p.previews || {};
      p.styles.forEach(id => { p.previews[id] = p.previews[id] || { lg: first, sm: first }; });
    }
  }

  Object.entries(shopify.products || {}).forEach(([h, live]) => attach(h, live));

  /* Anything with no Shopify data yet still needs a previews object, or
     shot() throws when a card asks for one. */
  cat.products.forEach(p => { p.previews = p.previews || {}; });

  /* Crests ship as flat theme assets; only Liquid knows the CDN prefix, so
     they are resolved here rather than baked in at build time. */
  /* assetBase sits on WSP_SHOPIFY itself, not inside .routes. */
  const assetBase = shopify.assetBase || '';
  cat.clubs.forEach(c => { if (c.crest) c.crest = assetBase + c.crest; });

  W.WSP_CATALOGUE = cat;

  /* ------------------------------------------------------------------
     2. Pull the whole catalogue when a page actually needs it
     Used by the design studio, which lets you switch player without a
     page load and therefore needs every variant id. /products.json is the
     Online Store's own endpoint and needs no app or token.
     ------------------------------------------------------------------ */
  let allPromise = null;
  function loadAllVariants() {
    if (allPromise) return allPromise;
    allPromise = (async () => {
      for (let page = 1; page <= 6; page++) {
        const res = await fetch(`${R.root || '/'}products.json?limit=250&page=${page}`, {
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) break;
        const { products } = await res.json();
        if (!products || !products.length) break;
        products.forEach(sp => attach(sp.handle, {
          id: sp.id, url: `${R.products || '/products/'}${sp.handle}`,
          available: sp.variants.some(v => v.available),
          images: (sp.images || []).map(i => i.src),
          variants: sp.variants.map(v => ({
            id: v.id, sku: v.sku, price: parseFloat(v.price),
            available: v.available, options: [v.option1, v.option2, v.option3],
          })),
        }));
        if (products.length < 250) break;
      }
      return cat;
    })();
    return allPromise;
  }

  /* ------------------------------------------------------------------
     3. Routes
     ------------------------------------------------------------------ */
  /* Clubs resolve through search rather than a collection per club. Every
     product is tagged with its club, so search returns exactly that club's
     prints — and no collection has to be created, or kept in step, as the
     catalogue grows. Swap these for collection URLs once the catalogue is
     large enough that curated collections earn their keep.

     Finish is a variant option, not a product facet, so it has no collection
     to point at: it goes to the studio, where you can actually pick one. */
  function wireRoutes() {
    if (!W.WSP) return;
    const root = R.root || '/';
    W.WSP.ROUTES.club    = cl => `${root}search?type=product&q=${encodeURIComponent(cl.name)}`;
    W.WSP.ROUTES.style   = s  => `${root}pages/customise?style=${encodeURIComponent(s.id)}`;
    W.WSP.ROUTES.product = p  => `${R.products || '/products/'}${handleOf(p)}`;
    W.WSP.loadAllVariants = loadAllVariants;
    W.WSP.shopify = shopify;
  }

  /* ------------------------------------------------------------------
     4. The basket becomes Shopify's cart
     The engraving is a line-item property on the print, plus one unit of
     the engraving add-on product to carry the £4.99 — line-item properties
     cannot hold a price, and all three option slots are spent on
     Style/Size/Framing.
     ------------------------------------------------------------------ */
  const money = n => '£' + Number(n).toFixed(2);

  async function cartState() {
    const res = await fetch(`${R.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
    return res.ok ? res.json() : { items: [], item_count: 0, total_price: 0 };
  }

  function paintCart(cart) {
    const $  = W.WSP.$, $$ = W.WSP.$$, esc = W.WSP.esc, el = W.WSP.el;
    $$('[data-basket-count]').forEach(e => { e.textContent = cart.item_count; e.hidden = cart.item_count === 0; });
    const total = $('[data-basket-total]');
    if (total) total.textContent = money(cart.total_price / 100);
    const go = $('[data-basket-checkout]');
    if (go) go.disabled = cart.item_count === 0;

    const body = $('[data-basket-body]');
    if (!body) return;
    body.innerHTML = cart.items.length ? '' :
      `<p class="small" style="padding:40px 0;color:var(--t3)">Your bag is empty.</p>`;

    cart.items.forEach(item => {
      const props = item.properties || {};
      const engraving = props.Engraving || props.engraving || '';
      const node = el('div', 'line', `
        <div class="line__shot"><img src="${esc(item.image || '')}" alt=""></div>
        <div style="flex:1;min-width:0">
          <div class="line__name">${esc(item.product_title)}</div>
          <div class="line__meta">${esc((item.options_with_values || []).map(o => o.value).join(' · '))}</div>
          ${engraving ? `<div class="line__meta">Engraved: “${esc(engraving)}”</div>` : ''}
          <div class="line__foot">
            <span class="small num">${money(item.final_line_price / 100)} × ${item.quantity}</span>
            <button class="line__rm" type="button">Remove</button>
          </div>
        </div>`);
      node.querySelector('.line__rm').addEventListener('click', async () => {
        await fetch(`${R.root || '/'}cart/change.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: 0 }),
        });
        paintCart(await cartState());
      });
      body.appendChild(node);
    });
  }

  function wireBasket() {
    if (!W.WSP) return;
    const basket = W.WSP.basket;

    basket.paint = async function () { paintCart(await cartState()); };

    basket.add = async function (line) {
      if (!line.variantId) {
        W.WSP.toast('That option is not available yet.');
        return;
      }
      const items = [{
        id: line.variantId,
        quantity: line.qty || 1,
        properties: line.dedication ? { Engraving: line.dedication } : undefined,
      }];
      /* The fee rides as its own line so the customer sees what they paid
         for, and so refunds and reporting can separate it from the print. */
      if (line.dedication && shopify.engraving && shopify.engraving.id) {
        items.push({
          id: shopify.engraving.id, quantity: line.qty || 1,
          properties: { 'For': line.name, Engraving: line.dedication },
        });
      }
      const res = await fetch(`${R.root || '/'}cart/add.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        W.WSP.toast(err.description || 'Could not add that to the bag.');
        return;
      }
      await basket.paint();
      W.WSP.toast(`${line.name} added to bag`);
      W.WSP.openDrawer();
    };
  }

  /* wsp.js defines window.WSP and then calls this, before it loads the
     catalogue or dispatches wsp:catalogue — so every section can rely on
     WSP.shopify and WSP.ROUTES already being in place. */
  W.WSP_THEME_INIT = function () {
    wireRoutes();
    wireBasket();
    W.WSP.basket.paint();
  };
})(window, document);
